import styled from "@emotion/styled";

const Section = styled.section`
  background-color: ${({ theme }) => theme.colors.tone.sand};
  color: ${({ theme }) => theme.colors.tone.black};
  padding-block: ${({ theme }) => theme.space.space32};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.space.space24};
  align-items: start;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: minmax(0, 392px) minmax(0, 600px);
    grid-template-rows: auto auto 1fr;
    justify-content: space-between;
    column-gap: ${({ theme }) => theme.space.space32};
    row-gap: ${({ theme }) => theme.space.space32};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.space16};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 1;
    grid-row: 1;
  }
`;

const Pill = styled.span`
  align-self: flex-start;
  background-color: ${({ theme }) => theme.colors.primary.goldLight};
  border-radius: 140px;
  padding-block: ${({ theme }) => theme.space.space4};
  padding-inline: ${({ theme }) => theme.space.space8};
  font-size: ${({ theme }) => theme.fontSizes.fontSize12};
  font-weight: 600;
  line-height: 16px;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.fontSize24};
  line-height: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.fontSize32};
    line-height: 40px;
  }
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.fontSize16};
  line-height: 24px;
`;

const Media = styled.div<{ hasStats: boolean }>`
  display: grid;
  padding-bottom: ${({ hasStats }) => (hasStats ? "72px" : "0")};

  & > * {
    grid-column: 1;
    grid-row: 1;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 2;
    grid-row: 1 / span 3;
    padding-bottom: ${({ theme, hasStats }) =>
      hasStats ? theme.space.space32 : "0"};
  }
`;

const Stats = styled.ul`
  align-self: end;
  z-index: 1;
  margin-block: 0 -72px;
  margin-inline: ${({ theme }) => theme.space.space8};
  padding: ${({ theme }) => theme.space.space16};
  list-style: none;
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.space.space16};
  background-color: ${({ theme }) => theme.colors.tone.white};
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: -${({ theme }) => theme.space.space32};
    margin-inline: ${({ theme }) => theme.space.space32};
    padding: ${({ theme }) => theme.space.space32};
    gap: ${({ theme }) => theme.space.space32};
  }
`;

const Stat = styled.li`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 0 1 auto;
  }
`;

const StatValue = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.fontSize24};
  line-height: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.fontSize32};
    line-height: 40px;
  }
`;

const StatCaption = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.tone.anthracite};
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  line-height: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.fontSize16};
    line-height: 24px;
  }
`;

const Checks = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.space8};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 1;
  }
`;

const CheckItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.space8};
  font-size: ${({ theme }) => theme.fontSizes.fontSize16};
  line-height: 24px;

  svg {
    flex-shrink: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.space16};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-column: 1;
    flex-direction: row;
    align-self: start;

    & > a:first-of-type {
      width: auto;
    }
  }
`;

const LoginLink = styled.a`
  color: ${({ theme }) => theme.colors.tone.black};
  font-size: ${({ theme }) => theme.fontSizes.fontSize16};
  font-weight: 600;
  line-height: 24px;
  text-decoration: underline;
`;

export {
  Section,
  Grid,
  TitleGroup,
  Pill,
  Title,
  Subtitle,
  Media,
  Stats,
  Stat,
  StatValue,
  StatCaption,
  Checks,
  CheckItem,
  Actions,
  LoginLink,
};
